import { parseLsToStudioModel } from "@lunaverse-ide/shared";
import { withProtagonist } from "@lunaverse-ide/shared/embedded-player";
import type { PlayEpisodeData } from "@lunaverse-ide/shared/embedded-player/react";
import type { StageState } from "@lunaverse-ide/shared/stage";

interface StepOp {
  readonly id: string;
  readonly type: string;
  readonly name?: string;
  readonly character?: string;
  readonly look?: string;
  readonly url?: string;
  readonly text?: string;
  readonly transition?: string;
}

const LINE_OP_TYPE: Record<string, "narrator" | "you" | "dialogue"> = {
  narration: "narrator",
  monologue: "you",
  dialogue: "dialogue",
};

/**
 * Turn the accumulated editor stage into the one atomic beat consumed by the
 * official Cocos monitor. This is ported from the latest Lunaverse IDE.
 */
export function stageEpisode(state: StageState, protagonist?: string | null): PlayEpisodeData {
  const beat: StepOp[] = [];

  if (state.bg) {
    beat.push({
      id: "mon_bg",
      type: "bg",
      name: state.bg.name,
      url: state.bg.assetPath ?? state.bg.imageUrl ?? "",
      transition: "cut",
    });
  }

  const lineType = LINE_OP_TYPE[state.cardKind];
  const lineText = state.dialogue?.text ?? "";
  if (lineType && lineText) {
    beat.push(
      lineType === "dialogue"
        ? { id: "mon_line", type: "dialogue", character: state.dialogue?.speaker ?? "", text: lineText }
        : { id: "mon_line", type: lineType, text: lineText },
    );
  }

  state.characters.forEach((character, index) => {
    beat.push({
      id: `mon_char_${index}`,
      type: "char_show",
      character: character.name,
      look: character.look,
      url: character.assetPath ?? character.imageUrl ?? "",
    });
  });

  const steps: Array<StepOp | StepOp[]> = beat.length > 0
    ? [beat]
    : [{ id: "mon_line", type: "narrator", text: "" }];
  const mcToken = state.characters.find((character) => character.lane === "left")?.name ?? protagonist;

  return withProtagonist({
    episode_id: "__monitor__",
    title: "",
    branch_key: "main",
    seq: 0,
    ending: null,
    gate: null,
    steps,
    autoContinueExternalSteps: false,
  }, mcToken);
}

interface CardSourceRange {
  readonly start: number;
  readonly end: number;
}

function studioCardRanges(source: string): CardSourceRange[] {
  const cards = parseLsToStudioModel(source).cards;
  if (cards.length === 0) return [];
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const ranges: CardSourceRange[] = [];
  let cursor = 0;

  for (const card of cards) {
    const cardLines = card.source.replace(/\r\n/g, "\n").split("\n");
    const first = cardLines.find((line) => line.trim().length > 0)?.trim();
    let startIndex = -1;
    if (first) {
      startIndex = lines.findIndex((line, index) => index >= cursor && line.trim() === first);
    }
    if (startIndex < 0) startIndex = Math.min(cursor, Math.max(0, lines.length - 1));
    const endIndex = Math.min(lines.length - 1, startIndex + Math.max(0, cardLines.length - 1));
    ranges.push({ start: startIndex + 1, end: endIndex + 1 });
    cursor = endIndex + 1;
  }
  return ranges;
}

/** Map a tolerant source row (including nested Choice rows) to its Studio card. */
export function studioCardIndexForLine(source: string, line: number): number {
  const ranges = studioCardRanges(source);
  if (ranges.length === 0) return 0;
  const exact = ranges.findIndex((range) => line >= range.start && line <= range.end);
  if (exact >= 0) return exact;
  let previous = 0;
  ranges.forEach((range, index) => {
    if (range.start <= line) previous = index;
  });
  return previous;
}
